/**
 * Projects — top-level container for cross-account work surfaces.
 *
 *   - Workflows  → kanban with one card per workflow
 *   - Agents     → placeholder (running/scheduled agents; lands in a follow-up)
 *   - Development → placeholder (engineering pipelines; lands in a follow-up)
 *
 * The Projects dropdown in the top menu deep-links to one of these
 * sub-views; otherwise the view boots on Workflows.
 */

import { useEffect, useRef, useState } from 'react';
import { Bot, Building2, Code2, FolderKanban, ListChecks, type LucideIcon } from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { C } from '../lib/tokens';
import { ClientsDirectoryView } from './ClientsDirectoryView';
import { ProjectsWorkspaceView } from './ProjectsWorkspaceView';
import { TasksBoardView } from './TasksBoardView';
import { AgentsView } from './AgentsView';
import { DevelopmentView } from './DevelopmentView';
import type { UseAgentScheduler } from '../lib/agent/agentScheduler';

// 'workflows' is retained in the union for back-compat with any saved deep-links;
// the module is hidden from the UI (WorkflowsKanbanView code is kept, not deleted).
export type ProjectsTab = 'clients' | 'projects' | 'workflows' | 'tasks' | 'agents' | 'development';

/**
 * Entity quick-jump target for the Projects surface (⌘K palette → onJump).
 * A task jump lands on the Tasks tab with its detail drawer open; an agent
 * jump lands on the Agents tab with its detail drawer open. The nonce lets
 * the same record be re-focused twice in a row.
 */
export interface ProjectsFocus {
  kind: 'task' | 'agent';
  id: string;
  nonce: number;
}

interface ProjectsViewProps {
  /** Initial sub-tab. Driven by the Projects top-menu dropdown. */
  initialTab?: ProjectsTab;
  /**
   * The app-wide agent-scheduler loop, owned by App so it runs regardless of
   * the open view. Passed through to the Agents tab for the master switch,
   * the live ticker, and per-card "next run" badges.
   */
  scheduler?: UseAgentScheduler;
  /** A task/agent to land on directly (from the ⌘K command palette). */
  focus?: ProjectsFocus | null;
}

interface TabMeta {
  id: ProjectsTab;
  label: string;
  icon: LucideIcon;
}

const tabs: TabMeta[] = [
  { id: 'clients', label: 'Clients', icon: Building2 },
  { id: 'projects', label: 'Projects', icon: FolderKanban },
  { id: 'tasks', label: 'Tasks', icon: ListChecks },
  { id: 'agents', label: 'Agents', icon: Bot },
  { id: 'development', label: 'Development', icon: Code2 },
];

export function ProjectsView({ initialTab = 'clients', scheduler, focus = null }: ProjectsViewProps) {
  const [tab, setTab] = useState<ProjectsTab>(initialTab);
  const lastInitial = useRef<ProjectsTab>(initialTab);
  const lastFocusNonce = useRef(0);

  // Re-sync when the dropdown re-fires with a new sub-tab.
  useEffect(() => {
    if (initialTab !== lastInitial.current) {
      setTab(initialTab);
      lastInitial.current = initialTab;
    }
  }, [initialTab]);

  // Entity quick-jump: switch to the tab that owns the chosen record. The
  // detail drawer is opened by the child panel from its own focus prop.
  useEffect(() => {
    if (!focus || focus.nonce === lastFocusNonce.current) return;
    lastFocusNonce.current = focus.nonce;
    setTab(focus.kind === 'agent' ? 'agents' : 'tasks');
  }, [focus]);

  // Workflows owns the fixed-height kanban layout — it needs the wide
  // shell. Tasks, Agents, and Development are card-grids that want the
  // width too.
  const wrapperClass =
    tab === 'clients' ||
    tab === 'projects' ||
    tab === 'tasks' ||
    tab === 'agents' ||
    tab === 'development'
      ? 'people-shell people-shell--wide'
      : 'app-container';

  return (
    <div className={wrapperClass} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Tabs */}
      <GlassPanel
        variant="subtle"
        animate={false}
        style={{ padding: '10px 14px', flex: '0 0 auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className="glass-btn"
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${active ? 'rgba(255,0,255,0.45)' : 'rgba(255,255,255,0.10)'}`,
                  background: active
                    ? 'linear-gradient(135deg, rgba(255,0,255,0.20), rgba(187,65,152,0.12))'
                    : 'transparent',
                  color: active ? '#fff' : C.textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Icon size={13} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </GlassPanel>

      {tab === 'clients' && <ClientsDirectoryView />}
      {tab === 'projects' && <ProjectsWorkspaceView />}
      {tab === 'tasks' && (
        <TasksBoardView
          focus={focus && focus.kind === 'task' ? { id: focus.id, nonce: focus.nonce } : null}
        />
      )}
      {/* The projects shell is height-locked for the kanban boards, which
          manage their own internal scroll. Agents and Development are long
          document-style views — without their own scroll region the content
          below the fold is simply unreachable. */}
      {tab === 'agents' && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <AgentsView
            scheduler={scheduler}
            focus={focus && focus.kind === 'agent' ? { id: focus.id, nonce: focus.nonce } : null}
          />
        </div>
      )}
      {tab === 'development' && (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <DevelopmentView onOpenAgents={() => setTab('agents')} />
        </div>
      )}
    </div>
  );
}
